import React, { useState, useMemo } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useSearchParams } from 'react-router-dom';
import { WORK_ORDERS_LIST, WORK_ORDER_DELETE } from '../../graphql/workOrders';
import Pagination from '../../components/Pagination';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  IconButton, 
  Button, 
  TableSortLabel, 
  Box, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  Typography,
  Chip,
  Avatar,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  Tooltip,
  CircularProgress,
  MenuItem
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Build,
  Warning,
  CheckCircle,
  Schedule,
  Person,
  Title,
  Category,
  CalendarToday,
  FactCheck,
  Link
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

// Work order categories for filtering
const workOrderCategories = [
  { value: 'All', label: 'All Categories' },
  { value: 'mechanical', label: 'Mechanical' },
  { value: 'electrical', label: 'Electrical' },
  { value: 'body_repair', label: 'Body Repair' },
  { value: 'tire_service', label: 'Tire Service' },
  { value: 'brake_service', label: 'Brake Service' },
  { value: 'engine_service', label: 'Engine Service' },
  { value: 'transmission', label: 'Transmission' },
  { value: 'suspension', label: 'Suspension' },
  { value: 'exhaust', label: 'Exhaust System' },
  { value: 'cooling_system', label: 'Cooling System' },
  { value: 'fuel_system', label: 'Fuel System' },
  { value: 'interior', label: 'Interior' },
  { value: 'exterior', label: 'Exterior' },
  { value: 'safety', label: 'Safety Equipment' },
  { value: 'other', label: 'Other' }
];

// Helper function to get category display name
const getCategoryDisplayName = (categoryValue) => {
  const category = workOrderCategories.find(cat => cat.value === categoryValue);
  return category ? category.label : categoryValue || 'Not Specified';
};

// Helper function to format inspection reference
const getInspectionReference = (inspection) => {
  if (!inspection) return null;
  return {
    title: inspection.inspector || 'Unknown Inspector',
    date: inspection.startDate ? dayjs(inspection.startDate).format('MMM DD, YYYY') : 'No date',
    id: inspection.id
  };
};

const columns = [
  { id: 'title', label: 'Title' },
  { id: 'category', label: 'Category' },
  { id: 'status', label: 'Status' },
  { id: 'dateOpened', label: 'Date Opened' },
  { id: 'daysOpen', label: 'Days Open' },
  { id: 'assignedTo', label: 'Assigned To' },
  { id: 'createdBy', label: 'Created By' },
  { id: 'inspection', label: 'Inspection Reference' },
];

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) return -1;
  if (b[orderBy] > a[orderBy]) return 1;
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'completed': return { bg: '#10b981', color: 'white' };
    case 'in progress': return { bg: '#f59e0b', color: 'white' };
    case 'open': return { bg: '#3b82f6', color: 'white' };
    case 'cancelled': return { bg: '#ef4444', color: 'white' };
    default: return { bg: '#e5e7eb', color: '#374151' };
  }
};

const getStatusIcon = (status) => {
  switch (status?.toLowerCase()) {
    case 'completed': return <CheckCircle sx={{ fontSize: 16 }} />;
    case 'in progress': return <Build sx={{ fontSize: 16 }} />;
    case 'open': return <Schedule sx={{ fontSize: 16 }} />;
    case 'cancelled': return <Warning sx={{ fontSize: 16 }} />;
    default: return <Schedule sx={{ fontSize: 16 }} />;
  }
};

const WorkOrderList = ({ onEdit }) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Get pagination and filter state from URL
  const currentPage = parseInt(searchParams.get('page')) || 1;
  const itemsPerPage = parseInt(searchParams.get('limit')) || 10;
  const search = searchParams.get('search') || '';
  const statusFilter = searchParams.get('status') || 'All';
  const categoryFilter = searchParams.get('category') || 'All';

  // Update URL parameters
  const updateURLParams = (updates) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value && value !== 'All' && value !== '') {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
    });
    setSearchParams(newParams);
  };

  // Handle filter changes
  const handleSearchChange = (value) => {
    updateURLParams({ search: value, page: 1 });
  };

  const handleStatusChange = (value) => {
    updateURLParams({ status: value, page: 1 });
  };

  const handleCategoryChange = (value) => {
    updateURLParams({ category: value, page: 1 });
  };

  const handlePageChange = (page) => {
    updateURLParams({ page });
  };

  const handleItemsPerPageChange = (limit) => {
    updateURLParams({ limit, page: 1 });
  };

  const { data, loading, error, refetch } = useQuery(WORK_ORDERS_LIST, {
    variables: { 
      first: itemsPerPage,
      skip: (currentPage - 1) * itemsPerPage,
      sort: { createdAt: 'DESC' } 
    }
  });
  const [deleteWorkOrder] = useMutation(WORK_ORDER_DELETE, { onCompleted: () => refetch() });
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('createdAt');
  const [deleteDialog, setDeleteDialog] = useState({ open: false, workOrder: null });
  const navigate = useNavigate();

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleEdit = (workOrder) => {
    if (onEdit) {
      onEdit(workOrder);
    } else {
      navigate(`/fleet/work-orders/${workOrder.id}`);
    }
  };

  const handleAdd = () => navigate('/fleet/work-orders/new');

  const handleDeleteClick = (workOrder) => setDeleteDialog({ open: true, workOrder });
  
  const handleDeleteConfirm = async () => {
    await deleteWorkOrder({ variables: { filter: { id: deleteDialog.workOrder.id } } });
    setDeleteDialog({ open: false, workOrder: null });
  };
  
  const handleDeleteCancel = () => setDeleteDialog({ open: false, workOrder: null });

  const statusOptions = ['All', 'Open', 'In Progress', 'Completed', 'Cancelled'];

  const filteredWorkOrders = useMemo(() => {
    if (!data?.workOrdersList?.items) return [];
    const workOrders = data.workOrdersList.items;
    return workOrders.filter((workOrder) => {
      const matchesSearch = 
        workOrder.title?.toLowerCase().includes(search.toLowerCase()) ||
        workOrder.description?.toLowerCase().includes(search.toLowerCase()) ||
        getCategoryDisplayName(workOrder.category).toLowerCase().includes(search.toLowerCase()) ||
        (workOrder.assignedTo?.firstName && workOrder.assignedTo.firstName.toLowerCase().includes(search.toLowerCase())) ||
        (workOrder.assignedTo?.lastName && workOrder.assignedTo.lastName.toLowerCase().includes(search.toLowerCase())) ||
        (workOrder.createdBy?.firstName && workOrder.createdBy.firstName.toLowerCase().includes(search.toLowerCase())) ||
        (workOrder.createdBy?.lastName && workOrder.createdBy.lastName.toLowerCase().includes(search.toLowerCase())) ||
        (workOrder.inspection?.inspector && workOrder.inspection.inspector.toLowerCase().includes(search.toLowerCase())) ||
        (workOrder.inspection?.notes && workOrder.inspection.notes.toLowerCase().includes(search.toLowerCase()));
      const matchesStatus = statusFilter === 'All' || workOrder.status === statusFilter;
      const matchesCategory = categoryFilter === 'All' || workOrder.category === categoryFilter;
      return matchesSearch && matchesStatus && matchesCategory;
    });
  }, [data, search, statusFilter, categoryFilter]);

  const sortedRows = [...filteredWorkOrders].sort(getComparator(order, orderBy));

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress size={40} />
      </Box>
    );
  }
  
  if (error) {
    return (
      <Card sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="error">Error loading work orders.</Typography>
      </Card>
    );
  }

  return (
    <Box>
      {/* Search and Filters */}
      <Card sx={{ 
        mb: 3, 
        borderRadius: 3, 
        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
        border: '1px solid #e2e8f0'
      }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <FilterIcon sx={{ mr: 1, color: 'text.secondary' }} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
              Search & Filters
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
            <TextField
              label="Search Work Orders"
              value={search}
              onChange={e => handleSearchChange(e.target.value)}
              placeholder="Search by title, description, category, personnel, or inspection"
              size="small"
              sx={{ minWidth: 300, flexGrow: 1 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              select
              label="Status"
              value={statusFilter}
              onChange={e => handleStatusChange(e.target.value)}
              size="small"
              sx={{ minWidth: 150 }}
            >
              {statusOptions.map(option => (
                <MenuItem key={option} value={option}>{option}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Category"
              value={categoryFilter}
              onChange={e => handleCategoryChange(e.target.value)}
              size="small"
              sx={{ minWidth: 180 }}
            >
              {workOrderCategories.map(option => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </TextField>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={handleAdd}
              sx={{
                background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)'
                }
              }}
            >
              Add Work Order
            </Button>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Found {data?.workOrdersList?.count || 0} work orders
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Work Order Table */}
      <Card sx={{ 
        borderRadius: 3, 
        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
        border: '1px solid #e2e8f0',
        overflow: 'hidden'
      }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8fafc' }}>
                <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Title</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Category</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Date Opened</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Days Open</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Assigned To</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Created By</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Inspection Reference</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedRows.map((row) => {
                const statusConfig = getStatusColor(row.status);
                const daysOpen = row.dateOpened ? dayjs().diff(dayjs(row.dateOpened), 'day') : 0;
                
                return (
                  <TableRow 
                    key={row.id} 
                    hover 
                    onClick={() => handleEdit(row)} 
                    sx={{
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        bgcolor: 'rgba(239, 68, 68, 0.04)',
                        transform: 'scale(1.01)',
                      }
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar 
                          sx={{ 
                            bgcolor: '#ef4444', 
                            width: 32, 
                            height: 32, 
                            mr: 2,
                            boxShadow: '0 2px 8px rgba(239, 68, 68, 0.3)'
                          }}
                        >
                          <Title sx={{ fontSize: 16 }} />
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                            {row.title || 'No title'}
                          </Typography>
                          {row.description && (
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                              {row.description.length > 50 ? `${row.description.substring(0, 50)}...` : row.description}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getCategoryDisplayName(row.category)}
                        size="small"
                        sx={{
                          bgcolor: '#e3f2fd',
                          color: '#1976d2',
                          fontWeight: 500
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getStatusIcon(row.status)}
                        label={row.status || 'Open'}
                        size="small"
                        sx={{
                          bgcolor: statusConfig.bg,
                          color: statusConfig.color,
                          fontWeight: 600,
                          '& .MuiChip-icon': {
                            color: statusConfig.color
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {row.dateOpened ? dayjs(row.dateOpened).format('MMM DD, YYYY') : 'Not set'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={`${daysOpen} days`}
                        size="small"
                        sx={{
                          bgcolor: daysOpen > 30 ? '#fee2e2' : daysOpen > 14 ? '#fef3c7' : '#d1fae5',
                          color: daysOpen > 30 ? '#dc2626' : daysOpen > 14 ? '#d97706' : '#059669',
                          fontWeight: 600
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar 
                          sx={{ 
                            bgcolor: '#6366f1', 
                            width: 24, 
                            height: 24, 
                            mr: 1,
                            boxShadow: '0 2px 8px rgba(99, 102, 241, 0.3)'
                          }}
                        >
                          <Person sx={{ fontSize: 12 }} />
                        </Avatar>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {row.assignedTo ? `${row.assignedTo.firstName} ${row.assignedTo.lastName}` : 'Unassigned'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar 
                          sx={{ 
                            bgcolor: '#10b981', 
                            width: 24, 
                            height: 24, 
                            mr: 1,
                            boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)'
                          }}
                        >
                          <Person sx={{ fontSize: 12 }} />
                        </Avatar>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {row.createdBy ? `${row.createdBy.firstName} ${row.createdBy.lastName}` : 'Unknown'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {row.inspection ? (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Avatar 
                            sx={{ 
                              bgcolor: '#3b82f6', 
                              width: 24, 
                              height: 24, 
                              mr: 1,
                              boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)'
                            }}
                          >
                            <FactCheck sx={{ fontSize: 12 }} />
                          </Avatar>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500, color: '#3b82f6' }}>
                              Created from Inspection
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                              {getInspectionReference(row.inspection)?.title} - {getInspectionReference(row.inspection)?.date}
                            </Typography>
                          </Box>
                        </Box>
                      ) : (
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                          No inspection reference
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="Edit Work Order">
                          <IconButton 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEdit(row);
                            }} 
                            size="small" 
                            sx={{
                              color: '#ef4444',
                              '&:hover': {
                                bgcolor: 'rgba(239, 68, 68, 0.1)'
                              }
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Work Order">
                          <IconButton 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteClick(row);
                            }} 
                            size="small" 
                            sx={{
                              color: '#ef4444',
                              '&:hover': {
                                bgcolor: 'rgba(239, 68, 68, 0.1)'
                              }
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* Pagination */}
        <Pagination
          currentPage={currentPage}
          totalPages={Math.ceil((data?.workOrdersList?.count || 0) / itemsPerPage)}
          totalItems={data?.workOrdersList?.count || 0}
          itemsPerPage={itemsPerPage}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          itemsPerPageOptions={[10, 25, 50, 100]}
        />
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={deleteDialog.open} 
        onClose={handleDeleteCancel}
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 60px rgba(0,0,0,0.1)'
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
          color: 'white',
          fontWeight: 600
        }}>
          Delete Work Order
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Typography>
            Are you sure you want to delete this work order? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleDeleteCancel} variant="outlined">
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error" 
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)'
              }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WorkOrderList; 